// PlaneDiagnosticsHUD — dev-only readout of ARCore's currently-detected
// planes. Bottom-right monospace panel showing plane count, plus per-plane
// id / orientation / area / world position. Subscribes to the WebXR
// driver's detectedPlanes store via useSyncExternalStore.
//
// Gated by main.ts (only mounted when planeMode=1 URL param OR
// import.meta.env.DEV). Production bundle tree-shakes this away.

import { useCallback, useMemo, useSyncExternalStore } from 'react'
import type {
  AnchorStatus,
  DetectedPlaneInfo,
  DetectedPlanesStore,
  SurfaceClass,
  WebXRSessionInfo,
} from '@/ar/webxr-driver'

interface PlaneDiagnosticsHUDProps {
  store: DetectedPlanesStore
  /** Read fresh each render — driver mutates .sessionInfo in place and
   *  fires the plane store listeners when it changes so we pick it up. */
  getSessionInfo: () => WebXRSessionInfo
}

export function PlaneDiagnosticsHUD({
  store,
  getSessionInfo,
}: PlaneDiagnosticsHUDProps) {
  const subscribe = useMemo(
    () => (cb: () => void) => store.subscribe(() => cb()),
    [store],
  )
  const getSnapshot = useCallback(() => store.getState(), [store])
  const planes = useSyncExternalStore(subscribe, getSnapshot)
  const info = getSessionInfo()

  const planeDetectionGranted = info.enabledFeatures.includes('plane-detection')
  const featuresColor = planeDetectionGranted ? '#73ff8b' : '#ffb6b6'
  const detectedColor =
    info.detectedPlanesType.startsWith('Set(') &&
    !info.detectedPlanesType.startsWith('Set(0)')
      ? '#73ff8b'
      : info.detectedPlanesType.startsWith('Set(0)')
        ? '#ffe27a'
        : '#ffb6b6'

  return (
    <div
      data-testid="plane-diagnostics-hud"
      style={{
        position: 'fixed',
        bottom: 6,
        right: 6,
        padding: '8px 10px',
        background: 'rgba(0, 0, 0, 0.78)',
        color: 'white',
        font: '11px/1.4 ui-monospace, "SF Mono", Menlo, monospace',
        borderRadius: 8,
        pointerEvents: 'none',
        zIndex: 100,
        minWidth: 260,
        maxWidth: 320,
        maxHeight: '70vh',
        overflowY: 'auto',
      }}
    >
      {/* --- Diagnostic block --- */}
      <div style={{ fontWeight: 'bold', color: '#ffe27a', marginBottom: 4 }}>
        WebXR diagnostics
      </div>
      <DiagRow
        label="plane-detection"
        value={planeDetectionGranted ? 'GRANTED' : 'NOT GRANTED'}
        color={featuresColor}
      />
      <DiagRow
        label="enabledFeatures"
        value={
          info.enabledFeatures.length > 0
            ? info.enabledFeatures.join(', ')
            : '(empty)'
        }
        small
      />
      <DiagRow
        label="detectedPlanes"
        value={info.detectedPlanesType}
        color={detectedColor}
      />
      <DiagRow label="UA" value={shortUA(info.userAgent)} small />

      {/* --- Current-surface classification (works without plane-detection) --- */}
      <div
        style={{
          fontWeight: 'bold',
          color: '#ffe27a',
          marginTop: 8,
          marginBottom: 4,
          paddingTop: 6,
          borderTop: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        Current surface
      </div>
      <DiagRow
        label="type"
        value={info.currentSurface.toUpperCase()}
        color={surfaceColor(info.currentSurface)}
      />
      <DiagRow
        label="normal.y"
        value={info.currentSurfaceNormalY.toFixed(3)}
        small
      />
      <div style={{ opacity: 0.55, fontSize: 10, marginTop: 2 }}>
        {surfaceRule(info.currentSurface)}
      </div>

      {/* --- Anchor status (diagnoses wheel-teleport root cause) --- */}
      <div
        style={{
          fontWeight: 'bold',
          color: '#ffe27a',
          marginTop: 8,
          marginBottom: 4,
          paddingTop: 6,
          borderTop: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        Anchors
      </div>
      <DiagRow
        label="feature"
        value={info.anchorStatus.featureGranted ? 'GRANTED' : 'NOT GRANTED'}
        color={info.anchorStatus.featureGranted ? '#73ff8b' : '#ffb6b6'}
      />
      <DiagRow
        label="created / landed"
        value={
          info.anchorStatus.created +
          ' / ' +
          info.anchorStatus.landed +
          (info.anchorStatus.rejected > 0
            ? ' (rej ' + info.anchorStatus.rejected + ')'
            : '')
        }
        color={
          info.anchorStatus.created > 0 && info.anchorStatus.landed === 0
            ? '#ffb6b6'
            : info.anchorStatus.landed > 0
              ? '#73ff8b'
              : '#ffffff'
        }
      />
      <DiagRow
        label="pose-lost frames"
        value={String(info.anchorStatus.poseLostFrames)}
        color={info.anchorStatus.poseLostFrames > 0 ? '#ffe27a' : '#ffffff'}
        small
      />
      {info.anchorStatus.firstAnchorInitialPos != null && (
        <DiagRow
          label="1st drift"
          value={info.anchorStatus.firstAnchorDriftMeters.toFixed(3) + 'm'}
          color={
            info.anchorStatus.firstAnchorDriftMeters > 0.1
              ? '#ffb6b6'
              : info.anchorStatus.firstAnchorDriftMeters > 0.02
                ? '#ffe27a'
                : '#73ff8b'
          }
        />
      )}
      <div style={{ opacity: 0.55, fontSize: 10, marginTop: 2 }}>
        {anchorHint(info.anchorStatus)}
      </div>

      {/* --- Planes block --- */}
      <div
        style={{
          fontWeight: 'bold',
          color: '#ffe27a',
          marginTop: 8,
          marginBottom: 4,
          paddingTop: 6,
          borderTop: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        ARCore planes: {planes.length}
      </div>
      {planes.length === 0 ? (
        <div style={{ opacity: 0.6, fontStyle: 'italic' }}>
          (none yet — move phone to give ARCore parallax on features)
        </div>
      ) : (
        planes.map((p) => <PlaneRow key={p.id} plane={p} />)
      )}
    </div>
  )
}

function DiagRow({
  label,
  value,
  color,
  small,
}: {
  label: string
  value: string
  color?: string
  small?: boolean
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 8,
        fontSize: small ? 10 : 11,
        opacity: small ? 0.8 : 1,
      }}
    >
      <span style={{ opacity: 0.6, flexShrink: 0 }}>{label}</span>
      <span
        style={{
          color: color ?? 'inherit',
          textAlign: 'right',
          wordBreak: 'break-all',
        }}
      >
        {value}
      </span>
    </div>
  )
}

/** Colour for surface classification — green for wall (placeable),
 *  red for floor/ceiling (not-placeable), yellow for skew, dim for none. */
function surfaceColor(s: SurfaceClass): string {
  switch (s) {
    case 'wall':
      return '#73ff8b'
    case 'floor':
    case 'ceiling':
      return '#ffb6b6'
    case 'skew':
      return '#ffe27a'
    default:
      return '#ffffff'
  }
}

/** Human-readable classification rule so the developer sees why the
 *  current surface got its label. Keeps the thresholds visible on-device. */
function surfaceRule(s: SurfaceClass): string {
  switch (s) {
    case 'wall':
      return '|normal.y| < 0.26 → vertical'
    case 'floor':
      return 'normal.y > 0.85 → horizontal, up'
    case 'ceiling':
      return 'normal.y < -0.85 → horizontal, down'
    case 'skew':
      return 'ambiguous — sloped, not classified'
    default:
      return '(waiting for first hit)'
  }
}

/** On-device triage hint for the Anchors block. Spells out which of
 *  the two teleport failure modes the current numbers point to, so we
 *  don't need to remember the decision tree while pointing a phone
 *  at a wall. */
function anchorHint(s: AnchorStatus): string {
  if (!s.featureGranted) {
    return "'anchors' NOT granted → wheel teleports on re-localization"
  }
  if (s.created === 0) return '(no wheels placed yet)'
  if (s.landed === 0) {
    return 'created but never landed → check console for reject error'
  }
  if (s.firstAnchorDriftMeters > 0.1) {
    return 'anchor drifting > 10cm → ARCore world-model unstable here'
  }
  if (s.poseLostFrames > 30) {
    return 'anchor pose lost ' + s.poseLostFrames + '× → tracking gaps'
  }
  return 'anchors healthy — teleport (if any) is upstream of anchoring'
}

/** Chrome-Android UA is very long — shorten to the useful parts. */
function shortUA(ua: string): string {
  const chromeMatch = /Chrome\/[\d.]+/.exec(ua)
  const androidMatch = /Android \d+/.exec(ua)
  const parts: string[] = []
  if (androidMatch) parts.push(androidMatch[0])
  if (chromeMatch) parts.push(chromeMatch[0])
  return parts.length > 0 ? parts.join(' · ') : ua.slice(0, 40)
}

function PlaneRow({ plane: p }: { plane: DetectedPlaneInfo }) {
  const orientationColor =
    p.orientation === 'vertical' ? '#73ff8b' : '#ffe066'
  return (
    <div
      style={{
        marginTop: 4,
        paddingTop: 4,
        borderTop: '1px solid rgba(255,255,255,0.15)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: orientationColor, fontWeight: 'bold' }}>
          {p.id}
        </span>
        <span style={{ opacity: 0.7 }}>
          {p.orientation === 'vertical' ? 'V' : 'H'} · {p.areaM2.toFixed(2)}m²
        </span>
      </div>
      <div style={{ opacity: 0.6, fontSize: 10 }}>
        pos ({fmt(p.position.x)}, {fmt(p.position.y)}, {fmt(p.position.z)}) ·
        {' '}
        {p.polygon.length}v
      </div>
    </div>
  )
}

function fmt(n: number): string {
  return n.toFixed(2)
}
